// WaterBottleCard Premium - Versión con información completa del proceso
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader,
  RefreshCw,
  Droplets,
  Recycle,
  Truck,
  Shield,
  Play,
  Star,
  Quote,
  CheckCircle,
  ArrowRight,
  Clock,
  MapPin,
  PhoneCall,
  Zap,
  Filter,
  Sun,
  Sparkles,
} from "lucide-react";
import { useSalesContext } from "../../../contexts/SalesContext";
import { useInventoryPublic } from "../../../hooks/useInventoryPublic";
import { formatPrice } from "../../../scripts/generalScript";
import Stepper from "../../ui/Stepper";
import toast from "react-hot-toast";
import WaterService from "../../landingPage/WaterService";

// Constantes
const BOTTLE_PRICE_RAW = 15000;
const BOTTLE_PRICE_FORMATTED = formatPrice(BOTTLE_PRICE_RAW);
const BOTTLE_PRICE_PER_LITER = `${formatPrice(BOTTLE_PRICE_RAW / 20)} x litro`;

export const WaterBottleCard = () => {
  const { selectedSugs, setSelectedSugs } = useSalesContext();
  const {
    jugs: jugStock,
    isLoaded: isStockLoaded,
    isRefreshing: isUpdatingStock,
    refreshStock,
    validateQuantity,
  } = useInventoryPublic();

  const [stockWarning, setStockWarning] = useState("");
  const [isMobile, setIsMobile] = useState(false);

  // Detectar móvil
  useEffect(() => {
    const checkBreakpoint = () => setIsMobile(window.innerWidth < 768);
    checkBreakpoint();
    window.addEventListener("resize", checkBreakpoint);
    return () => window.removeEventListener("resize", checkBreakpoint);
  }, []);

  // Manejar cambio de cantidad con validación de stock
  const handleQuantityChange = (newQuantity) => {
    if (!isStockLoaded) {
      toast.error("Esperando información de inventario...");
      return;
    }

    const validation = validateQuantity("jugs", newQuantity);
    if (!validation.isValid) {
      toast.error(`Solo hay ${validation.max} botellones disponibles`);
      return;
    }

    setSelectedSugs(newQuantity);
  };

  const maxJugs = jugStock || 0;

  // 6 Etapas de purificación
  const purificationSteps = [
    {
      step: 1,
      name: "Filtración Sedimentos Gruesos",
      description: "Elimina partículas visibles, arena y sedimentos grandes",
      icon: <Filter className="w-6 h-6" />,
      color: "bg-blue-100 text-blue-600",
    },
    {
      step: 2,
      name: "Carbón Activado",
      description: "Remueve cloro, químicos y mejora sabor y olor",
      icon: <Sparkles className="w-6 h-6" />,
      color: "bg-gray-100 text-gray-600",
    },
    {
      step: 3,
      name: "Filtración Sedimentos Finos",
      description: "Captura partículas microscópicas restantes",
      icon: <Droplets className="w-6 h-6" />,
      color: "bg-cyan-100 text-cyan-600",
    },
    {
      step: 4,
      name: "Ósmosis Inversa",
      description: "Elimina hasta 99.9% de contaminantes disueltos",
      icon: <Shield className="w-6 h-6" />,
      color: "bg-green-100 text-green-600",
    },
    {
      step: 5,
      name: "Luz Ultravioleta",
      description: "Destruye bacterias, virus y microorganismos",
      icon: <Sun className="w-6 h-6" />,
      color: "bg-yellow-100 text-yellow-600",
    },
    {
      step: 6,
      name: "Ozonización",
      description: "Desinfección final y conservación natural",
      icon: <Zap className="w-6 h-6" />,
      color: "bg-purple-100 text-purple-600",
    },
  ];

  // Testimonios de clientes actualizados
  const testimonials = [
    {
      text: "Solo pido por la app y llega a mi casa al día siguiente. No tengo que cargar botellones pesados nunca más.",
      author: "María González",
      benefit: "Conveniencia",
    },
    {
      text: "El agua se nota súper pura y el sistema de recambio es genial. Ellos se encargan de todo.",
      author: "Carlos Rodríguez",
      benefit: "Calidad",
    },
    {
      text: "Desde que uso Fluenti no compro agua embotellada. Es más económico y ecológico.",
      author: "Ana Martínez",
      benefit: "Ahorro",
    },
  ];

  // Beneficios del botellón PET
  const petBenefits = [
    { icon: <Shield className="w-5 h-5" />, text: "100% libre de BPA" },
    { icon: <Recycle className="w-5 h-5" />, text: "Material PET reciclable" },
    { icon: <CheckCircle className="w-5 h-5" />, text: "Apto uso alimentario" },
    {
      icon: <Droplets className="w-5 h-5" />,
      text: "Conserva pureza del agua",
    },
  ];

  // Conveniencias del servicio
  const serviceConveniences = [
    {
      icon: <PhoneCall className="w-8 h-8" />,
      title: "Solo tienes que pedir",
      description: "Haz tu pedido desde la Web o Whatsapp",
    },
    {
      icon: <Truck className="w-8 h-8" />,
      title: "Donde lo necesites",
      description: "Entregamos en tu hogar u oficina",
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Sin salir de casa",
      description: "Programa tu entrega y recibe sin complicaciones",
    },
    {
      icon: <Recycle className="w-8 h-8" />,
      title: "Recambio automático",
      description: "Sistema circular: devuelves vacío, recibes lleno",
    },
  ];

  // Stats del sistema circular actualizados
  const sustainabilityStats = [
    { number: "300+", label: "Recambios realizados" },
    { number: "50+", label: "Usos promedio por botellón" },
    { number: "85%", label: "Menos plástico nuevo" },
    { number: "0", label: "Botellones en vertederos" },
  ];

  if (!isStockLoaded) {
    return (
      <div className="flex items-center justify-center p-8 h-96">
        <Loader className="animate-spin mr-2" />
        <span>Cargando información del producto...</span>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      {/* Overlay de carga */}
      <AnimatePresence>
        {isUpdatingStock && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50"
          >
            <div className="flex items-center gap-3 text-blue-600 bg-white px-6 py-3 rounded-xl ">
              <Loader className="animate-spin" size={24} />
              <span className="text-basefont-medium">
                Actualizando inventario...
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* SECCIÓN 1: PRODUCTO PRINCIPAL - Dos columnas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 mb-12 lg:mb-16">
        {/* Columna Izquierda - Información */}
        <div className="flex flex-col gap-8 order-2">
          {/* Título Principal */}
          <div className="flex flex-col gap-6">
            {/* Badges mejorados */}
            {/* <div className="flex flex-wrap gap-2">
              <span className="bg-gray-700 text-white font-base px-3 py-1 rounded-full text-sm">
                20L • PET
              </span>
              <span className="bg-gray-700 text-white font-base px-3 py-1 rounded-full text-sm">
                Botellón Retornable
              </span>
              <span className="bg-gray-700 text-white font-base px-3 py-1 rounded-full text-sm">
                6 Etapas de Purificación
              </span>
            </div> */}
            <h1 className="text-4xl font-bold fraunces lg:text-6xl  text-gray-900 leading-9 mb-3">
              Botellón Agua Purificada PET 20lt
            </h1>
          </div>

          {/* PRECIO */}
          <div className="flex items-end w-full justify-between ">
            <div className="min-w-1/2">
              <p className="text-3xl font-medium text-gray-900">
                ${BOTTLE_PRICE_FORMATTED}
              </p>
              <p className="text-gray-600">${BOTTLE_PRICE_PER_LITER}</p>
              <div className="flex items-center gap-2">
                <div
                  className={`w-3 h-3 rounded-full ${
                    jugStock > 10
                      ? "bg-green-500"
                      : jugStock > 0
                      ? "bg-yellow-500"
                      : "bg-red-500"
                  }`}
                />
                <p className="text-sm text-gray-600">{jugStock} disponibles</p>
              </div>
            </div>
            <div className="w-1/2">
              <Stepper
                value={selectedSugs}
                onChange={handleQuantityChange}
                min={0}
                max={maxJugs}
              />
            </div>
          </div>
          <div className="space-y-4">
            <div className="w-full flex flex-col p-4 gap-4 bg-stone-50 rounded-3xl">
              <div className="bg-white rounded-xl p-4">
                <div className="text-sm">
                  <p className="font-medium text-blue-900">
                    ♻️ Sistema de préstamo retornable
                  </p>
                  <p className="text-blue-700 mt-1">
                    Recámbialo por uno lleno o retórnalo cuando termines. No
                    retornarlo genera el cobro del botellón.
                    <a
                      className="underline font-semibold hover:text-blue-800 ml-1"
                      href="/terminos"
                      target="_blank"
                    >
                      Ver terminos
                    </a>
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4">
                <div className="text-sm">
                  <p className="font-medium text-green-900">
                    🚚 Entrega gratuita en zona principal
                  </p>
                  <p className="text-green-700 mt-1">
                    Solo pides y nosotros llevamos donde necesites.
                    <a
                      className="underline hover:text-green-800 font-semibold ml-1"
                      href="/cobertura"
                      target="_blank"
                    >
                      Ver cobertura
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Columna Derecha - Imagen Principal */}
        <div className="">
          <div className="flex-shrink-0 flex items-center justify-center w-full rounded-3xl h-48 md:h-[500px]">
            <img
              className="w-full h-full object-contain"
              src="/assets/img/landing/full-sug-drops-blue-plan-recambios-botellon-20l-dispensador-pedidos-online-entrega-domicilio-agua-purificada-calidad.png"
              alt="Botellón PET 20L Retornable"
            />
          </div>
        </div>
      </div>

      {/* SECCIÓN 2: CONVENIENCIA DEL SERVICIO */}
      <div className="bg-stone-50 py-12 px-8 mb-12 rounded-3xl">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 fraunces">
              La conveniencia que buscabas
            </h2>
            <p className="text-basetext-gray-600 max-w-3xl mx-auto">
              Olvídate de cargar botellones pesados, hacer filas o quedarte sin
              agua. Con Fluenti, solo pides y nosotros nos encargamos del resto.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {serviceConveniences.map((convenience, index) => (
              <div key={index} className="text-center space-y-4">
                <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                  {convenience.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 fraunces">
                  {convenience.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {convenience.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SECCIÓN 2.5: BOTELLÓN PET PREMIUM CON LÍNEAS APUNTANDO */}
      <WaterService />

      {/* SECCIÓN 3: 6 ETAPAS DE PURIFICACIÓN */}
      <div className="text-center  rounded-3xl bg-stone-50 p-8 my-12">
        <h2 className="text-2xl lg:text-4xl font-bold text-gray-900 mb-4 fraunces">
          Proceso de purificación
          <span className="block text-blue-600">en 6 etapas</span>
        </h2>
        <p className="text-base text-gray-600 max-w-3xl mx-auto mb-8">
          Cada gota pasa por nuestro riguroso proceso tecnológico para
          garantizar la máxima pureza, eliminando el 99.9% de contaminantes.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {purificationSteps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl p-6 text-center space-y-4"
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <div
                  className={`w-12 h-12 rounded-full ${step.color} flex items-center justify-center`}
                >
                  {step.icon}
                </div>
                <div className="bg-gray-100 text-gray-700 w-8 h-8 rounded-full flex items-center justify-center font-medium">
                  {step.step}
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 fraunces">{step.name}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {step.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>


      {/* SECCIÓN 4: TESTIMONIOS */}
      <div className="bg-blue-50 text-gray-800 py-12 px-8 mb-12 rounded-3xl">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl lg:text-4xl font-bold fraunces mb-3">
              Lo que más valoran nuestros clientes
            </h2>
            <div className="flex justify-center items-center gap-2">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className="w-5 h-5 fill-yellow-400 text-yellow-400"
                />
              ))}
              <span className="ml-2 text-base font-medium">
                4.9/5 en conveniencia
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-blue-100 bg-opacity-50 rounded-xl p-6 text-center"
              >
                <div className="bg-white bg-opacity-20 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <Quote className="w-6 h-6" />
                </div>
                <p className="text-base mb-4 leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div className="border-t border-white border-opacity-20 pt-4">
                  <p className="font-medium">— {testimonial.author}</p>
                  <p className="text-blue-800 text-sm">{testimonial.benefit}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SECCIÓN 5: SUSTENTABILIDAD */}
      <div className="bg-green-50 text-green-800 py-12 px-8 mb-12 rounded-3xl">
        <div className="max-w-6xl mx-auto text-center">
          <div>
            <h2 className="text-3xl lg:text-4xl font-bold fraunces mb-4 leading-tight">
              Sistema circular inteligente
            </h2>
            <p className="text-basemb-8 max-w-3xl mx-auto leading-relaxed">
              Cada botellón PET se reutiliza múltiples veces en nuestro sistema
              retornable, reduciendo drasticamente la producción de plástico
              nuevo y eliminando residuos.
            </p>
          </div>

          {/* Stats mejorados */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {sustainabilityStats.map((stat, index) => (
              <div key={index} className="text-center py-4">
                <div className="text-3xl lg:text-4xl font-medium mb-2">
                  {stat.number}
                </div>
                <div className="text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SECCIÓN 6: PRODUCTOS RELACIONADOS */}
      {/* <div className="text-center mb-12">
        <h2 className="text-2xl lg:text-3xl font-medium text-gray-900 mb-8">
          Completa tu experiencia de hidratación
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          <div className="bg-gray-900 text-white rounded-2xl p-6 text-center">
            <img
              src="https://placehold.co/250x200/1f2937/ffffff?text=Dispensador+Premium"
              alt="Dispensador"
              className="w-full h-40 object-cover rounded-xl mb-4"
            />
            <h3 className="text-xl font-medium mb-2">Dispensador Premium</h3>
            <p className="text-gray-300 mb-4">
              Incluido gratis con tu primer botellón
            </p>
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="text-2xl font-bold">Gratis</span>
              <span className="text-gray-400 line-through text-sm">
                $13.000
              </span>
            </div>
            <button className="w-full bg-white text-gray-900 py-2 rounded-xl font-medium hover:bg-gray-100 transition-colors">
              Agregar gratis
            </button>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center">
            <img
              src="https://placehold.co/250x200/374151/ffffff?text=Certificaciones+INVIMA"
              alt="Certificaciones"
              className="w-full h-40 object-cover rounded-xl mb-4"
            />
            <h3 className="text-xl font-medium mb-2 text-gray-900">
              Certificado INVIMA
            </h3>
            <p className="text-gray-600 mb-4">
              Calidad y pureza garantizada oficialmente
            </p>
            <button className="w-full bg-blue-600 text-white py-2 rounded-xl font-medium hover:bg-blue-700 transition-colors">
              Ver certificado
            </button>
          </div>

          <div className="bg-blue-600 text-white rounded-2xl p-6 text-center">
            <img
              src="https://placehold.co/250x200/2563eb/ffffff?text=Cobertura+Barranquilla"
              alt="Cobertura Barranquilla"
              className="w-full h-40 object-cover rounded-xl mb-4"
            />
            <h3 className="text-xl font-medium mb-2">Llegamos donde estés</h3>
            <p className="text-blue-100 mb-4">
              Zona principal: gratis • Zona extendida: $3.500
            </p>
            <button className="w-full bg-white text-blue-600 py-2 rounded-xl font-medium hover:bg-gray-100 transition-colors">
              Verificar tu zona
            </button>
          </div>
        </div>
      </div> */}

      {/* Botón de refresh */}
      {!isStockLoaded && (
        <div className="text-center mt-8">
          <button
            onClick={refreshStock}
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors"
            disabled={isUpdatingStock}
          >
            <RefreshCw
              size={16}
              className={isUpdatingStock ? "animate-spin" : ""}
            />
            {isUpdatingStock ? "Actualizando..." : "Actualizar disponibilidad"}
          </button>
        </div>
      )}
    </div>
  );
};
